import { useState } from "react";
import { AuthContextProps, useAuth } from "react-oidc-context";

import { useMutation, useQueries, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";

class SilentRefreshFailedError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "SilentRefreshFailedError";
	}
}

export class HttpError extends Error {
	constructor(
		message: string,
		public status: number
	) {
		super(message);
		this.name = "HttpError";
	}
}

// Returns the token from signinSilent itself: auth.user is captured at render time and still holds the old token
async function retrySilentRefresh(auth: AuthContextProps, maxAttempts = 3, delay = 1000): Promise<string> {
	for (let attempt = 0; ; attempt++) {
		try {
			const user = await auth.signinSilent();
			if (!user?.access_token) {
				throw new Error("signinSilent returned no user");
			}
			return user.access_token;
		} catch (error) {
			if (attempt >= maxAttempts - 1) {
				throw new SilentRefreshFailedError(`Silent refresh failed after multiple attempts, ${error}`); // Throw custom error if all attempts failed
			}
			await new Promise((resolve) => setTimeout(resolve, delay * Math.pow(2, attempt))); // Exponential backoff
		}
	}
}

// Refresh before calling Strømbestilling instead of sending a token we already know is expired
async function getValidToken(auth: AuthContextProps): Promise<string> {
	if (auth.user?.access_token && !auth.user.expired) {
		return auth.user.access_token;
	}
	return retrySilentRefresh(auth);
}

async function fetchStrombestilling<T>(auth: AuthContextProps, endpoint: string): Promise<T> {
	const makeRequest = async (token: string) => {
		const response = await fetch(`${import.meta.env.VITE_STROEMBESTILLING_API_URL}${endpoint}`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				Accept: "application/json",
			},
		});

		if (response.status === 401) {
			throw new HttpError("Unauthorized", 401);
		}

		if (!response.ok) {
			throw new HttpError(`HTTP error! status: ${response.status}`, response.status);
		}

		return (await response.json()) as T;
	};

	try {
		return await makeRequest(await getValidToken(auth));
	} catch (error) {
		if (!(error instanceof HttpError && error.status === 401)) {
			throw error;
		}
		// Token rejected even though it looked valid (revoked, clock skew): refresh once and retry. A second 401 is final
		return await makeRequest(await retrySilentRefresh(auth));
	}
}

const queryOptions = {
	retry: (failureCount: number, error: Error) => {
		if (error instanceof SilentRefreshFailedError) {
			return false; // Do not retry if silent refresh failed
		}
		// Don't retry client errors (4xx) except 408, 429 which may be transient. 401 is already retried once with a fresh token in queryFn
		if (error instanceof HttpError && error.status >= 400 && error.status < 500) {
			if (![408, 429].includes(error.status)) {
				return false;
			}
		}
		return failureCount < 3; // Allow two retries for other errors (total 3 attempts)
	},
	retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
	// Disable automatic refetch behaviors to prevent 401 spam when token expires
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	refetchOnMount: false,
};

export function useStrombestillingApiQuery<T>(
	endpoint: string,
	key: string[],
	enabled: boolean = true,
	// Polling, e.g. while waiting for a status to change. Only polls while the tab is visible
	refetchInterval?: UseQueryOptions<T, Error>["refetchInterval"]
): UseQueryResult<T, Error> {
	const auth = useAuth();
	const [previousData, setPreviousData] = useState<T | undefined>(undefined);

	const query = useQuery<T, Error>({
		queryKey: key,
		queryFn: async () => {
			const data = await fetchStrombestilling<T>(auth, endpoint);
			setPreviousData(data);
			return data;
		},
		enabled: enabled && !!auth.user?.access_token,
		refetchInterval,
		...queryOptions,
	});

	return {
		...query,
		data: query.isLoading ? previousData : query.data,
	} as UseQueryResult<T, Error>;
}

/** Several GET requests at once, e.g. one per item in a list, with the same token handling and retries as useStrombestillingApiQuery */
export function useStrombestillingApiQueries<T>(requests: { endpoint: string; key: string[] }[]): UseQueryResult<T, Error>[] {
	const auth = useAuth();
	return useQueries({
		queries: requests.map(({ endpoint, key }) => ({
			queryKey: key,
			queryFn: () => fetchStrombestilling<T>(auth, endpoint),
			enabled: !!auth.user?.access_token,
			...queryOptions,
		})),
	});
}

export function useStrombestillingApiMutation(endpoint: string, invalidatesKey: string[], method: string = "PUT", refetchInactive: boolean = false) {
	const queryClient = useQueryClient();
	const auth = useAuth();

	return useMutation({
		mutationFn: async (body: unknown) =>
			fetch(`${import.meta.env.VITE_STROEMBESTILLING_API_URL}${endpoint}`, {
				method,
				headers: {
					"Authorization": `Bearer ${await getValidToken(auth)}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify(body),
			}),
		onSuccess: () => {
			// Invalidate queries with a matching key, for instance "products"
			queryClient.invalidateQueries({
				queryKey: invalidatesKey,
				...(refetchInactive ? { refetchType: "all" } : {}),
			});
		},
		onError: (error) => {
			console.error(error);
		},
		retry: (failureCount, error) => {
			if (error instanceof SilentRefreshFailedError) {
				return false; // Do not retry if silent refresh failed
			}
			// Don't retry client errors (4xx) except 401, 408, 429 which may be transient
			if (error instanceof HttpError && error.status >= 400 && error.status < 500) {
				if (![401, 408, 429].includes(error.status)) {
					return false;
				}
			}
			return failureCount < 3; // Allow two retries for other errors (total 3 attempts)
		},
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	});
}
